// src/app/gen2ai/google-image.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  ref,
  set,
  update,
  remove,
  onValue,
  DatabaseReference
} from 'firebase/database';
import { database } from '../gen2ai/fire-init';
import {
  getAuth,
  onAuthStateChanged,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  User
} from 'firebase/auth';
import { HttpClient } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';

export type ImageCategory =
  | 'Screenshots'
  | 'Diagrams'
  | 'UX/UI'
  | 'Marketing'
  | 'Misc';

export interface ImageItem {
  id: string;
  userId: string;

  title: string;
  description: string;

  imageUrl: string;
  storagePath: string;
  fileType: 'png' | 'jpg' | 'jpeg' | 'gif' | 'webp' | 'other';

  primaryCategory: ImageCategory;
  labels: string[];

  pinned: boolean;
  archived: boolean;
  color: string;
  order: number;

  createdAt: number;
  updatedAt: number;
}

interface UploadUserImageResponse {
  imageId: string;
  userId: string;
  fileName: string;
  mimeType: string;
  gcsPath: string;
  publicUrl: string;
  error?: string;
  details?: string;
}

const REQUIRED_CATEGORIES: ImageCategory[] = [
  'Screenshots',
  'Diagrams',
  'UX/UI',
  'Marketing',
  'Misc'
];

@Injectable({ providedIn: 'root' })
export class GoogleImageService {
  private auth = getAuth();

  private imagesRef: DatabaseReference | null = null;

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  private imagesSubject = new BehaviorSubject<ImageItem[]>([]);
  images$ = this.imagesSubject.asObservable();

  // Clarichat integration: currently selected image to send
  private clarichatImageSubject = new BehaviorSubject<ImageItem | null>(null);
  clarichatImage$ = this.clarichatImageSubject.asObservable();

  private initialized = false;

  /**
   * Cloud Function endpoint for uploadUserImage
   * update to your deployed URL:
   * e.g. https://us-central1-clarichatai.cloudfunctions.net/uploadUserImage
   */
  private readonly uploadEndpoint =
    'https://us-central1-clarichatai.cloudfunctions.net/uploadUserImage';

  constructor(private http: HttpClient) {
    this.initAuthListener();
  }

  /* ------------------------------------------------------------------ */
  /*  Auth                                                              */
  /* ------------------------------------------------------------------ */

  private initAuthListener(): void {
    if (this.initialized) return;
    this.initialized = true;

    onAuthStateChanged(this.auth, async (user) => {
      if (!user) {
        const cred = await signInAnonymously(this.auth);
        user = cred.user;
      }
      this.userSubject.next(user);
      this.attachImagesListener(user.uid);
    });
  }

  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(this.auth, provider);
  }

  async signOut(): Promise<void> {
    await this.auth.signOut();
    this.userSubject.next(null);
    this.imagesSubject.next([]);
    this.imagesRef = null;
  }

  private attachImagesListener(uid: string): void {
    this.imagesRef = ref(database, `images/${uid}`);
    onValue(this.imagesRef, (snapshot) => {
      const images: ImageItem[] = [];
      const data = snapshot.val();
      if (data) {
        Object.keys(data).forEach((id) => {
          images.push(data[id] as ImageItem);
        });
      }
      images.sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return a.order - b.order;
      });
      this.imagesSubject.next(images);
    });
  }

  private ensureUserId(): string {
    const user = this.auth.currentUser;
    if (!user) throw new Error('User not initialized yet');
    return user.uid;
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers                                                           */
  /* ------------------------------------------------------------------ */

  private validateCategory(primaryCategory: ImageCategory): ImageCategory {
    if (!REQUIRED_CATEGORIES.includes(primaryCategory)) {
      return 'Misc';
    }
    return primaryCategory;
  }

  private detectFileType(file: File): ImageItem['fileType'] {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return ext as ImageItem['fileType'];
    }
    return 'other';
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',', 2)[1] : result;
        resolve(base64);
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  CRUD + upload via Cloud Function                                  */
  /* ------------------------------------------------------------------ */

  async createImage(
    partial: {
      title: string;
      description: string;
      primaryCategory: ImageCategory;
      extraLabels?: string[];
      color?: string;
      pinned?: boolean;
    },
    file: File
  ): Promise<string> {
    const uid = this.ensureUserId();
    const id = uuidv4();
    const now = Date.now();

    const primaryCategory = this.validateCategory(partial.primaryCategory);
    const fileType = this.detectFileType(file);

    const extraLabels = (partial.extraLabels || [])
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    const labelsSet = new Set<string>([primaryCategory, ...extraLabels]);
    const labels = Array.from(labelsSet);

    // Call Cloud Function to upload to GCS
    const base64 = await this.fileToBase64(file);
    const payload = {
      userId: uid,
      imageId: id,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      imageBase64: base64
    };

    const resp = await firstValueFrom(
      this.http.post<UploadUserImageResponse>(this.uploadEndpoint, payload)
    );

    if (resp.error || resp.details) {
      throw new Error(
        resp.details || resp.error || 'Cloud Function returned an error.'
      );
    }

    const imageUrl = resp.publicUrl;
    const storagePath = resp.gcsPath;

    const image: ImageItem = {
      id,
      userId: uid,
      title: partial.title || '',
      description: partial.description || '',
      imageUrl,
      storagePath,
      fileType,
      primaryCategory,
      labels,
      pinned: !!partial.pinned,
      archived: false,
      color: partial.color || '#020617',
      order: now,
      createdAt: now,
      updatedAt: now
    };

    await set(ref(database, `images/${uid}/${id}`), image);
    return id;
  }

  async updateImage(id: string, changes: Partial<ImageItem>): Promise<void> {
    const uid = this.ensureUserId();
    const now = Date.now();
    await update(ref(database, `images/${uid}/${id}`), {
      ...changes,
      updatedAt: now
    });
  }

  async deleteImage(id: string): Promise<void> {
    const uid = this.ensureUserId();
    await remove(ref(database, `images/${uid}/${id}`));
    // Optionally: call another Cloud Function to delete from GCS.
  }

  async togglePinned(id: string, pinned: boolean): Promise<void> {
    await this.updateImage(id, { pinned });
  }

  async toggleArchived(id: string, archived: boolean): Promise<void> {
    await this.updateImage(id, { archived });
  }

  async reorderImages(orderKeys: string[]): Promise<void> {
    const uid = this.ensureUserId();
    const updates: any = {};
    orderKeys.forEach((id, idx) => {
      updates[`images/${uid}/${id}/order`] = idx + 1;
    });
    await update(ref(database), updates);
  }

  /* ------------------------------------------------------------------ */
  /*  Clarichat integration                                             */
  /* ------------------------------------------------------------------ */

  setClarichatImage(image: ImageItem | null): void {
    this.clarichatImageSubject.next(image);
  }
}
